import { FC, useContext, useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { states } from "capitalrx-types";
import { Checkbox, Dropdown, FileEntry, FileUpload, RadioGroup, TextArea, Textbox } from "capitalrx-components-2.0/lib/forms";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Heading, Paragraph, Semibold } from "capitalrx-components-2.0/lib/typography";
import { Button, Icon } from "capitalrx-components-2.0/lib/general";
import { validate } from "capitalrx-components-2.0";
import { formatDate } from "capitalrx-utilities";

import { ProviderManagement } from "@/api/interfaces";
import { PROVIDER_MANAGEMENT_LAYOUT } from "@/constants";
import { TinField } from "@/components";
import { ThemeSelectorContext } from "@/context";
import { getThemeButton, npiValidator, formatZipCode } from "@/utils";
import { getDocumentsByType } from "../helper";


export interface BaseAddress {
  readonly street_address: string;
  readonly address_line: string;
  readonly city: string;
  readonly state: string;
  readonly zip_code: string;
}

export interface ProviderDetailsDataForm {
  /* Provider Details
     // Identification */
  readonly provider_type: ProviderManagement.ProviderTypeValue;
  readonly tin: string;
  readonly designation: ProviderManagement.DesignationTypeValue;
  readonly medicare_id_number: string;
  readonly medicaid_id_number: string;
  readonly npi: string;

  // // Business Details
  readonly legal_business_name: string;
  readonly doing_business_name: string;
  readonly ncpdp_number: string;
  readonly relationship_id: string;

  // // Physical Address
  readonly physical: BaseAddress;

  // // Mailing Address
  readonly mailing: BaseAddress & {
    readonly check_same_physical_address: boolean;
  };
  readonly w9_form: {
    files: FileEntry[];
    notes: string;
  };
}

export function checkSamePhysicalAddress(provider: ProviderManagement.ProviderSchema) {
  if (
    provider.mailing_address_line_1 === provider.physical_address_line_1
    && provider.mailing_address_line_2 === provider.physical_address_line_2
    && provider.mailing_address_city === provider.physical_address_city
    && provider.mailing_address_state === provider.physical_address_state
    && provider.mailing_address_zip_code === provider.physical_address_zip_code
  ) {
    return true;
  }
  return false;
}

export interface DetailsGroupFieldsProps {
  readonly readOnly?: boolean;
  readonly provider: ProviderManagement.ProviderSchema;
  readonly documents: ProviderManagement.DocumentSchema[];
  readonly onEdit?: () => void;
}

export const DetailsGroupFields: FC<DetailsGroupFieldsProps> = ({ readOnly, provider, documents, onEdit }) => {
  const { threeFull, sixFull, twelve, isCompressed } = useResponsive(PROVIDER_MANAGEMENT_LAYOUT);
  const StackOrShelf = isCompressed ? Stack : Shelf;
  const { primary_color, secondary_hover_color } = useContext(ThemeSelectorContext);

  const { setValue, getValues, watch, control } = useFormContext<ProviderDetailsDataForm>();
  const mailingCheckSamePhysicalAddress = useWatch({
    control,
    name: "mailing.check_same_physical_address",
  });

  useEffect(() => {
    const { unsubscribe } = watch((value, { type, name }) => {
      const setPhysicalAddress = () => {
        setValue("mailing.street_address", value.physical.street_address);
        setValue("mailing.address_line", value.physical.address_line);
        setValue("mailing.city", value.physical.city);
        setValue("mailing.state", value.physical.state);
        setValue("mailing.zip_code", value.physical.zip_code);
      };
      const physicalAddressFields = [
        "physical.street_address",
        "physical.address_line",
        "physical.city",
        "physical.state",
        "physical.zip_code",
      ];
      if (type === "change" && physicalAddressFields.includes(name) && value.mailing.check_same_physical_address) {
        setPhysicalAddress();
      } else if (type === "change" && name === "mailing.check_same_physical_address") {
        if (value.mailing.check_same_physical_address) {
          setPhysicalAddress();
        } else {
          setValue("mailing.street_address", "");
          setValue("mailing.address_line", "");
          setValue("mailing.city", "");
          setValue("mailing.state", "");
          setValue("mailing.zip_code", "");
        }
      }
    });
    return () => {
      unsubscribe();
    };
  }, [setValue, getValues, watch]);

  // Format zip code fields: physical and mailing zip code
  const [physicalZip, mailingZip] = useWatch({
    control,
    name: ["physical.zip_code", "mailing.zip_code"],
  });

  // Apply formatting when values change
  useEffect(() => {
    const ZipFields = [
      { value: physicalZip, name: "physical.zip_code" },
      { value: mailingZip, name: "mailing.zip_code" },
    ];

    ZipFields.forEach(({ value, name }) => {
      if (value !== undefined) {
        const f = formatZipCode(value);
        if (f !== value) {
          (setValue as any)(name, f, { shouldValidate: false });
        }
      }
    });
  }, [physicalZip, mailingZip, setValue]);

  const documentsByType = useMemo(() => {
    return getDocumentsByType(documents);
  }, [documents]);

  const displayDocument = (document: ProviderManagement.DocumentSchema) => {
    return (
      <Stack spacing="mini">
        <StackOrShelf spacing="small">
          <Shelf spacing="small">
            <Icon name="document" />
            <Body>{document.file_name}</Body>
            <Shelf.Spacer />
          </Shelf>
          <Shelf.Spacer />
          <Body>{formatDate(document.created_at, "MM/DD/YYYY hh:mm:ss A", true, true)}</Body>
        </StackOrShelf>
        <StackOrShelf spacing="small">
          <Semibold>Notes: </Semibold>
          <Paragraph>{document.notes}</Paragraph>
          <Shelf.Spacer />
        </StackOrShelf>
      </Stack>
    );
  };

  return (
    <Stack spacing="large">
      <Stack>
        <Shelf>
          <Heading>Identification</Heading>
          <Shelf.Spacer />
          {readOnly && onEdit && (
            <Button
              icon="edit"
              appearance={getThemeButton("secondary", primary_color, secondary_hover_color)}
              onClick={onEdit}
            >
              Edit
            </Button>
          )}
        </Shelf>
        <Grid>
          <Grid.Spread columns={sixFull}>
            <TinField readOnly formName="tin" designationName="designation" />
            <RadioGroup.Field label="Designation" name="designation" testId="designation" orientation="horizontal" readOnly>
              <RadioGroup.Option value={ProviderManagement.DESIGNATION_TYPE_VALUES.SSN}>SSN</RadioGroup.Option>
              <RadioGroup.Option value={ProviderManagement.DESIGNATION_TYPE_VALUES.EIN}>EIN</RadioGroup.Option>
            </RadioGroup.Field>
          </Grid.Spread>
          <Grid.Break />
          <Grid.Spread columns={sixFull}>
            <Textbox.Field label="NPI (Billing)" name="npi" testId="npi" validator={npiValidator} required readOnly />
          </Grid.Spread>
          <Grid.Break />
          <Grid.Spread columns={sixFull}>
            <Textbox.Field label="Medicare ID Number" name="medicare_id_number" testId="medicare_id_number" readOnly={readOnly} />
            <Textbox.Field label="Medicaid ID Number" name="medicaid_id_number" testId="medicaid_id_number" readOnly={readOnly} />
          </Grid.Spread>
        </Grid>
      </Stack>
      <Stack>
        <Heading>Business Details</Heading>
        <Grid>
          <Grid.Spread columns={sixFull}>
            <Textbox.Field
              label="Provider Name (Legal Business Name)"
              name="legal_business_name"
              testId="legal_business_name"
              required
              readOnly={readOnly}
            />
            <Textbox.Field
              label="Doing Business As Name (DBA)"
              name="doing_business_name"
              testId="doing_business_name"
              required
              readOnly={readOnly}
            />
          </Grid.Spread>
          <Grid.Break />
          {provider.type === ProviderManagement.PROVIDER_TYPE_VALUES.PHARMACY && (
            <Grid.Spread columns={sixFull}>
              <Textbox.Field label="NCPDP ID (NABP)" name="ncpdp_number" testId="ncpdp_number" required readOnly />
              <Textbox.Field label="Relationship ID (Chain Code)" name="relationship_id" testId="relationship_id" required readOnly />
            </Grid.Spread>
          )}
        </Grid>
      </Stack>
      <Stack>
        <Heading>Physical Address</Heading>
        <Grid>
          <Grid.Spread columns={twelve}>
            <Textbox.Field
              label="Street Address"
              name="physical.street_address"
              testId="physical_street_address"
              required
              readOnly={readOnly}
            />
            <Textbox.Field label="Address Line 2" name="physical.address_line" testId="physical_address_line" readOnly={readOnly} />
          </Grid.Spread>
          <Grid.Break />
          <Grid.Span columns={sixFull}>
            <Textbox.Field label="City" name="physical.city" testId="physical_city" required readOnly={readOnly} />
          </Grid.Span>
          <Grid.Span columns={threeFull}>
            <Dropdown.Field label="State" name="physical.state" testId="physical_state" required readOnly={readOnly} searchable>
              {Object.entries(states).map(([full, abbreviation]) => {
                return (
                  <Dropdown.Option key={abbreviation} value={abbreviation}>
                    {full}
                  </Dropdown.Option>
                );
              })}
            </Dropdown.Field>
          </Grid.Span>
          <Grid.Span columns={threeFull}>
            <Textbox.Field
              label="Zip Code"
              name="physical.zip_code"
              testId="physical_zip_code"
              validator={(value) => validate.isZipCode(value)}
              required
              readOnly={readOnly}
            />
          </Grid.Span>
        </Grid>
      </Stack>
      <Stack>
        <Heading>Mailing Address</Heading>
        <Grid>
          <Grid.Spread columns={twelve}>
            <Checkbox.Field
              label="Same as Physical Address"
              name="mailing.check_same_physical_address"
              testId="mailing_check_same_physical_address"
              hideLabel
              readOnly={readOnly}
            />
          </Grid.Spread>
          {!mailingCheckSamePhysicalAddress
            && <Grid>
              <Grid.Spread columns={twelve}>
                <Textbox.Field
                  label="Street Address"
                  name="mailing.street_address"
                  testId="mailing_street_address"
                  required
                  readOnly={readOnly || mailingCheckSamePhysicalAddress}
                />
                <Textbox.Field
                  label="Address Line 2"
                  name="mailing.address_line"
                  testId="mailing_address_line"
                  readOnly={readOnly || mailingCheckSamePhysicalAddress}
                />
              </Grid.Spread>
              <Grid.Break />
              <Grid.Span columns={sixFull}>
                <Textbox.Field
                  label="City"
                  name="mailing.city"
                  testId="mailing_city"
                  required
                  readOnly={readOnly || mailingCheckSamePhysicalAddress}
                />
              </Grid.Span>
              <Grid.Spread columns={threeFull}>
                <Dropdown.Field
                  label="State"
                  name="mailing.state"
                  testId="mailing_state"
                  required
                  searchable
                  readOnly={readOnly || mailingCheckSamePhysicalAddress}
                >
                  {Object.entries(states).map(([full, abbreviation]) => {
                    return (
                      <Dropdown.Option key={abbreviation} value={abbreviation}>
                        {full}
                      </Dropdown.Option>
                    );
                  })}
                </Dropdown.Field>
                <Textbox.Field
                  label="Zip Code"
                  name="mailing.zip_code"
                  testId="mailing_zip_code"
                  validator={(value) => validate.isZipCode(value)}
                  required
                  readOnly={readOnly || mailingCheckSamePhysicalAddress}
                />
              </Grid.Spread>
            </Grid>
          }
        </Grid>
      </Stack>
      <Stack>
        <Heading>W-9 Form</Heading>
        {documentsByType.w9 && displayDocument(documentsByType.w9)}
        {!readOnly
          && <Grid>
            <Grid.Spread columns={twelve}>
              <FileUpload.Field
                name="w9_form.files"
                allowMultiple={true}
                allowedExtensions={[".pdf", ".jpeg", ".jpg", ".png"]}
                testId="w9_form_files"
                label="Select File(s)"
                readOnly={readOnly}
                maxSize={4500000}
                required={provider.type === ProviderManagement.PROVIDER_TYPE_VALUES.MEDICAL && !documentsByType.w9}
              />
              <TextArea.Field label="Notes" testId="w9_form_notes" name="w9_form.notes" lines={6} readOnly={readOnly} />
            </Grid.Spread>
          </Grid>
        }
      </Stack>
    </Stack>
  );
};
